import { useContext, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { EmploymentIncomeSchema, EmploymentIncomeType } from '../../types';
import { styles } from '../../../../styles';
import { FinancialStatusContext } from '../../context/financial-status-context';
import Button from '../../../../_sharedComponents/button';
import ErrorIcon from '../../../../_sharedComponents/error-icon';
import StepHeader from '../../step-header';
import { formatter } from '../../../../../_utils/format';
import ErrorMessage from '../error-message';
import Label from '../label';

export default function EmploymentIncome() {
	const financialContext = useContext(FinancialStatusContext);
	const [totalEmploymentIncome, setTotalEmploymentIncome] = useState(0);
	const [totalOtherIncome, setTotalOtherIncome] = useState(0);

	const defaultValues: EmploymentIncomeType = {
		employment: {
			employmentStatus: 'Unemployed',
			employmentType: undefined,
			employerName: '',
			hourlyRate: undefined,
			hoursWorkedWeekly: undefined,
			spouseEmploymentStatus: 'No',
			spouseEmploymentType: undefined,
			spouseEmployerName: '',
			spouseHourlyRate: undefined,
			spouseHoursWorkedWeekly: undefined,
		},
		income: {
			retirement: 0,
			unemployment: 0,
			alimony: 0,
			childSupport: 0,
			ssiDisability: 0,
			publicAssistance: 0,
			otherIncome: 0,
		},
		userEnteredIncome: 0,
		explanation: '',
	};

	const {
		register,
		handleSubmit,
		control,
		watch,
		setValue,
		formState: { errors },
	} = useForm<EmploymentIncomeType>({
		mode: 'onSubmit',
		reValidateMode: 'onChange',
		resolver: zodResolver(EmploymentIncomeSchema),
		defaultValues,
	});

	const onSubmit = (data: EmploymentIncomeType) => {
		financialContext.updateEmploymentIncomeData(data);
		financialContext.updateCurrentStep(5);
		scrollTo({ top: 0, behavior: 'smooth' });
	};

	// Watch the employment status to conditionally render fields
	const employmentStatus = watch('employment.employmentStatus');
	const spouseEmploymentStatus = watch('employment.spouseEmploymentStatus');

	useEffect(() => {
		// Log all errors when any change occurs in the errors object
		if (Object.keys(errors).length > 0) {
			console.log('Form Errors:', errors);
		}
	}, [errors]);

	useEffect(() => {
		// Calculate total employment income
		const hourlyRate = watch('employment.hourlyRate') || 0;
		const hoursWorkedWeekly = watch('employment.hoursWorkedWeekly') || 0;
		const spouseHourlyRate = watch('employment.spouseHourlyRate') || 0;
		const spouseHoursWorkedWeekly = watch('employment.spouseHoursWorkedWeekly') || 0;

		const personalIncome = hourlyRate * hoursWorkedWeekly * 26;
		const spouseIncome = spouseHourlyRate * spouseHoursWorkedWeekly * 26;
		setTotalEmploymentIncome(personalIncome + spouseIncome);
	}, [
		watch('employment.hourlyRate'),
		watch('employment.hoursWorkedWeekly'),
		watch('employment.spouseHourlyRate'),
		watch('employment.spouseHoursWorkedWeekly'),
	]);

	useEffect(() => {
		// Calculate total other income
		const otherIncomeFields: (keyof EmploymentIncomeType['income'])[] = [
			'retirement',
			'unemployment',
			'alimony',
			'childSupport',
			'ssiDisability',
			'publicAssistance',
			'otherIncome',
		];

		let total = 0;
		otherIncomeFields.forEach((field) => {
			total += (watch(`income.${field}`) || 0) * 6;
		});
		setTotalOtherIncome(total);
	}, [
		watch('income.retirement'),
		watch('income.unemployment'),
		watch('income.alimony'),
		watch('income.childSupport'),
		watch('income.ssiDisability'),
		watch('income.publicAssistance'),
		watch('income.otherIncome'),
	]);

	return (
		<form className="h-full" onSubmit={handleSubmit(onSubmit)}>
			<div className="flex h-full flex-col justify-between">
				<StepHeader step={4} />
				<div className="min-h-24">
					<Label currentStep="employment" value="employmentStatus" />
					<div className="flex space-x-3">
						<Controller
							control={control}
							name="employment.employmentStatus"
							render={({ field: { onChange, onBlur, value, ref } }) => (
								<>
									{financialContext.jsonData.employment.radioQuestions?.[0]?.options.map((option) => (
										<div className="flex items-center space-x-2" key={option.en}>
											<input
												checked={value === option.en}
												id={option.en}
												onBlur={onBlur}
												onChange={() => {
													onChange(option.en);
													// Reset other dependent fields if needed
													setValue('employment.employmentType', undefined);
													setValue('employment.employerName', '');
													setValue('employment.hourlyRate', undefined);
													setValue('employment.hoursWorkedWeekly', undefined);
												}}
												ref={ref}
												type="radio"
												value={option.en}
											/>
											<label className={styles.radioLabel} htmlFor={option.en}>
												{option.en}
											</label>
										</div>
									))}
								</>
							)}
						/>
						{errors.employment?.employmentStatus ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="employment" value="employmentStatus" />
							</div>
						) : null}
					</div>
				</div>

				{/* Conditionally render employment-related questions */}
				{employmentStatus === 'Employed' ? (
					<div className="flex flex-col space-y-4">
						<div className="min-h-10 text-base font-light text-gray-500">
							Fill out the following completely and truthfully.
						</div>
						{/* Row 1 */}
						<div>
							<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
								{/* EmployerName */}
								<div>
									<Label currentStep="employment" value="employerName" />
									<div className="relative">
										<input
											{...register('employment.employerName')}
											className={`${styles.mediumInput} ${errors.employment?.employerName ? styles.error : ''}`}
											id="employerName"
											placeholder={financialContext.getPlaceholder({ step: 'employment', value: 'employerName' })}
											type="text"
										/>
										{errors.employment?.employerName ? <ErrorIcon /> : null}
									</div>
									{errors.employment?.employerName ? (
										<div className="md:max-w-56">
											<ErrorMessage currentStep="employment" value="employerName" />
										</div>
									) : null}
								</div>
								{/* Employment Type */}
								<div>
									<Label currentStep="employment" value="employmentType" />
									<div className="mt-2 flex space-x-3">
										{financialContext.jsonData.employment.radioQuestions?.[1]?.options.map((option) => (
											<div className="flex items-center space-x-2" key={option.en}>
												<input
													{...register('employment.employmentType')}
													id={option.en}
													type="radio"
													value={option.en}
												/>
												<label className={styles.radioLabel} htmlFor={option.en}>
													{option.en}
												</label>
											</div>
										))}
									</div>
									{errors.employment?.employmentType ? (
										<div>
											<ErrorMessage currentStep="employment" value="employmentType" />
										</div>
									) : null}
								</div>
							</div>
						</div>
						{/* Row 2 */}
						<div>
							<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
								{/* Hourly rate */}
								<div>
									<Label currentStep="employment" value="hourlyRate" />

									<input
										{...register(`employment.hourlyRate`, { valueAsNumber: true })}
										className={`${styles.mediumInput} ${errors.employment?.hourlyRate ? styles.error : ''}`}
										id="hourlyRate"
										placeholder={financialContext.getPlaceholder({ step: 'employment', value: 'hourlyRate' })}
										type="number"
									/>

									{errors.employment?.hourlyRate ? (
										<div className="md:max-w-56">
											<ErrorMessage currentStep="employment" value="hourlyRate" />
										</div>
									) : null}
								</div>
								{/* Hours Worked Weekly */}
								<div>
									<Label currentStep="employment" value="hoursWorkedWeekly" />

									<input
										{...register(`employment.hoursWorkedWeekly`, { valueAsNumber: true })}
										className={`${styles.mediumInput} ${errors.employment?.hoursWorkedWeekly ? styles.error : ''}`}
										id="hoursWorkedWeekly"
										placeholder={financialContext.getPlaceholder({ step: 'employment', value: 'hoursWorkedWeekly' })}
										type="number"
									/>

									{errors.employment?.hoursWorkedWeekly ? (
										<div className="md:max-w-56">
											<ErrorMessage currentStep="employment" value="hoursWorkedWeekly" />
										</div>
									) : null}
								</div>
							</div>
						</div>
					</div>
				) : null}

				{/* Spouse Employment Status */}
				<div className="mt-5 min-h-24">
					<Label currentStep="employment" value="spouseEmploymentStatus" />
					<div className="flex space-x-3">
						<Controller
							control={control}
							name="employment.spouseEmploymentStatus"
							render={({ field: { onChange, onBlur, value, ref } }) => (
								<>
									{financialContext.jsonData.employment.radioQuestions?.[2]?.options.map((option) => (
										<div className="flex items-center space-x-2" key={option.en}>
											<input
												checked={value === option.en}
												id={option.en}
												onBlur={onBlur}
												onChange={() => {
													onChange(option.en);
													// Reset other dependent fields if needed
													setValue('employment.spouseEmploymentType', undefined);
													setValue('employment.spouseEmployerName', '');
													setValue('employment.spouseHourlyRate', undefined);
													setValue('employment.spouseHoursWorkedWeekly', undefined);
												}}
												ref={ref}
												type="radio"
												value={option.en}
											/>
											<label className={styles.radioLabel} htmlFor={option.en}>
												{option.en}
											</label>
										</div>
									))}
								</>
							)}
						/>
						{errors.employment?.spouseEmploymentStatus ? (
							<div>
								<ErrorMessage currentStep="employment" value="spouseEmploymentStatus" />
							</div>
						) : null}
					</div>
				</div>

				{spouseEmploymentStatus === 'Yes' ? (
					<div className="flex flex-col space-y-4">
						<div>
							<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
								{/* Spouse Employer Name */}
								<div>
									<Label currentStep="employment" value="spouseEmployerName" />
									<div className="relative">
										<input
											{...register('employment.spouseEmployerName')}
											className={`${styles.mediumInput} ${errors.employment?.spouseEmployerName ? styles.error : ''}`}
											id="spouseEmployerName"
											placeholder={financialContext.getPlaceholder({ step: 'employment', value: 'spouseEmployerName' })}
											type="text"
										/>
										{errors.employment?.spouseEmployerName ? <ErrorIcon /> : null}
									</div>
									{errors.employment?.spouseEmployerName ? (
										<div className="md:max-w-56">
											<ErrorMessage currentStep="employment" value="spouseEmployerName" />
										</div>
									) : null}
								</div>
								{/* Spouse Employment Type */}
								<div>
									<Label currentStep="employment" value="spouseEmploymentType" />
									<div className="mt-2 flex space-x-3">
										{financialContext.jsonData.employment.radioQuestions?.[1]?.options.map((option) => (
											<div className="flex items-center space-x-2" key={option.en}>
												<input
													{...register('employment.spouseEmploymentType')}
													id={option.en}
													type="radio"
													value={option.en}
												/>
												<label className={styles.radioLabel} htmlFor={option.en}>
													{option.en}
												</label>
											</div>
										))}
									</div>
									{errors.employment?.spouseEmploymentType ? (
										<div className="mt-2">
											<ErrorMessage currentStep="employment" value="spouseEmploymentType" />
										</div>
									) : null}
								</div>
							</div>
						</div>
						<div>
							<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
								{/* Spouse Hourly Rate */}
								<div>
									<Label currentStep="employment" value="spouseHourlyRate" />

									<input
										{...register(`employment.spouseHourlyRate`, { valueAsNumber: true })}
										className={`${styles.mediumInput} ${errors.employment?.spouseHourlyRate ? styles.error : ''}`}
										id="spouseHourlyRate"
										placeholder={financialContext.getPlaceholder({ step: 'employment', value: 'spouseHourlyRate' })}
										type="number"
									/>

									{errors.employment?.spouseHourlyRate ? (
										<div className="md:max-w-56">
											<ErrorMessage currentStep="employment" value="spouseHourlyRate" />
										</div>
									) : null}
								</div>
								{/* Spouse Hours Worked Weekly */}
								<div>
									<Label currentStep="employment" value="spouseHoursWorkedWeekly" />

									<input
										{...register(`employment.spouseHoursWorkedWeekly`, { valueAsNumber: true })}
										className={`${styles.mediumInput} ${errors.employment?.spouseHoursWorkedWeekly ? styles.error : ''}`}
										id="spouseHoursWorkedWeekly"
										placeholder={financialContext.getPlaceholder({
											step: 'employment',
											value: 'spouseHoursWorkedWeekly',
										})}
										type="number"
									/>

									{errors.employment?.spouseHoursWorkedWeekly ? (
										<div className="md:max-w-56">
											<ErrorMessage currentStep="employment" value="spouseHoursWorkedWeekly" />
										</div>
									) : null}
								</div>
							</div>
						</div>
					</div>
				) : null}
				<div className={`my-5 ${styles.pageHeader}`}>
					{financialContext.language === 'en' ? 'Other Income' : 'Otros Ingresos'}
				</div>
				<div className="flex flex-col space-y-4">
					<div>
						<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
							{/* Retirement */}
							<div>
								<Label currentStep="income" value="retirement" />
								<Controller
									control={control}
									name="income.retirement"
									render={({ field }) => (
										<input
											{...field}
											className={`${styles.mediumInput} ${errors.income?.retirement ? styles.error : ''}`}
											id="retirement"
											onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
											placeholder={financialContext.getPlaceholder({ step: 'income', value: 'retirement' })}
											type="number"
											value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
										/>
									)}
								/>
								{errors.income?.retirement ? (
									<div className="md:max-w-56">
										<ErrorMessage currentStep="income" value="retirement" />
									</div>
								) : null}
							</div>
							{/* Unemployment */}
							<div>
								<Label currentStep="income" value="unemployment" />
								<Controller
									control={control}
									name="income.unemployment"
									render={({ field }) => (
										<input
											{...field}
											className={`${styles.mediumInput} ${errors.income?.unemployment ? styles.error : ''}`}
											id="unemployment"
											onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
											placeholder={financialContext.getPlaceholder({ step: 'income', value: 'unemployment' })}
											type="number"
											value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
										/>
									)}
								/>
								{errors.income?.unemployment ? (
									<div className="md:max-w-56">
										<ErrorMessage currentStep="income" value="unemployment" />
									</div>
								) : null}
							</div>
						</div>
					</div>
					{/* Alimony & Child Support*/}
					<div>
						<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
							{/* Alimony */}
							<div>
								<Label currentStep="income" value="alimony" />
								<Controller
									control={control}
									name="income.alimony"
									render={({ field }) => (
										<input
											{...field}
											className={`${styles.mediumInput} ${errors.income?.alimony ? styles.error : ''}`}
											id="alimony"
											onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
											placeholder={financialContext.getPlaceholder({ step: 'income', value: 'alimony' })}
											type="number"
											value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
										/>
									)}
								/>
								{errors.income?.alimony ? (
									<div className="md:max-w-56">
										<ErrorMessage currentStep="income" value="alimony" />
									</div>
								) : null}
							</div>
							{/* Child Support */}
							<div>
								<Label currentStep="income" value="childSupport" />
								<Controller
									control={control}
									name="income.childSupport"
									render={({ field }) => (
										<input
											{...field}
											className={`${styles.mediumInput} ${errors.income?.childSupport ? styles.error : ''}`}
											id="childSupport"
											onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
											placeholder={financialContext.getPlaceholder({ step: 'income', value: 'childSupport' })}
											type="number"
											value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
										/>
									)}
								/>
								{errors.income?.childSupport ? (
									<div className="md:max-w-56">
										<ErrorMessage currentStep="income" value="childSupport" />
									</div>
								) : null}
							</div>
						</div>
					</div>
					{/* SSI / Public Assistance */}
					<div>
						<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
							{/* SSI/DIsability */}
							<div>
								<Label currentStep="income" value="ssiDisability" />
								<Controller
									control={control}
									name="income.ssiDisability"
									render={({ field }) => (
										<input
											{...field}
											className={`${styles.mediumInput} ${errors.income?.ssiDisability ? styles.error : ''}`}
											id="ssiDisability"
											onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
											placeholder={financialContext.getPlaceholder({ step: 'income', value: 'ssiDisability' })}
											type="number"
											value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
										/>
									)}
								/>
								{errors.income?.ssiDisability ? (
									<div className="md:max-w-56">
										<ErrorMessage currentStep="income" value="ssiDisability" />
									</div>
								) : null}
							</div>
							{/* Public Assistance */}
							<div>
								<Label currentStep="income" value="publicAssistance" />
								<Controller
									control={control}
									name="income.publicAssistance"
									render={({ field }) => (
										<input
											{...field}
											className={`${styles.mediumInput} ${errors.income?.publicAssistance ? styles.error : ''}`}
											id="publicAssistance"
											onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
											placeholder={financialContext.getPlaceholder({ step: 'income', value: 'publicAssistance' })}
											type="number"
											value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
										/>
									)}
								/>
								{errors.income?.publicAssistance ? (
									<div className="md:max-w-56">
										<ErrorMessage currentStep="income" value="publicAssistance" />
									</div>
								) : null}
							</div>
						</div>
					</div>
					{/* Other */}
					<div>
						<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
							{/* Alimony */}
							<div>
								<Label currentStep="income" value="otherIncome" />
								<Controller
									control={control}
									name="income.otherIncome"
									render={({ field }) => (
										<input
											{...field}
											className={`${styles.mediumInput} ${errors.income?.otherIncome ? styles.error : ''}`}
											id="otherIncome"
											onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
											placeholder={financialContext.getPlaceholder({ step: 'income', value: 'otherIncome' })}
											type="number"
											value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
										/>
									)}
								/>
								{errors.income?.otherIncome ? (
									<div className="md:max-w-56">
										<ErrorMessage currentStep="income" value="otherIncome" />
									</div>
								) : null}
							</div>
						</div>
					</div>
				</div>
				<div className="mb-10 space-y-3">
					<div className="w-full items-center justify-between font-light text-gray-500 lg:flex lg:w-3/4">
						<div>Estimated Income from employment sources during the last 6 months:</div>
						<div className="font-semibold">{formatter.format(totalEmploymentIncome)}</div>
					</div>
					<div className="w-full items-center justify-between font-light text-gray-500 lg:flex lg:w-3/4">
						<div>Estimated income from other sources during the last 6 months:</div>
						<div className="font-semibold">{formatter.format(totalOtherIncome)}</div>
					</div>
					<div className="w-full items-center justify-between font-light text-gray-500 lg:flex lg:w-3/4">
						<div>(Combined Estimate):</div>
						<div className="font-semibold">{formatter.format(totalEmploymentIncome + totalOtherIncome)}</div>
					</div>
				</div>

				<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
					{/* Alimony */}
					<div>
						<Label currentStep="income" value="userEnteredIncome" />
						<Controller
							control={control}
							name="userEnteredIncome"
							render={({ field }) => (
								<input
									{...field}
									className={`${styles.mediumInput} ${errors.userEnteredIncome ? styles.error : ''}`}
									id="userEnteredIncome"
									onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
									placeholder={`${totalEmploymentIncome + totalOtherIncome}`}
									type="number"
									value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
								/>
							)}
						/>
						{errors.userEnteredIncome ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="income" value="userEnteredIncome" />
							</div>
						) : null}
					</div>
				</div>

				<div className="mx-auto my-10 flex space-x-10">
					<Button
						label={financialContext.language === 'en' ? 'Prev' : 'Anterior'}
						onClick={() => {
							financialContext.updateCurrentStep(3);
							scrollTo({ top: 0, behavior: 'smooth' });
						}}
						type="button"
					/>

					<Button label={financialContext.language === 'en' ? 'Next' : 'Seguiente'} type="submit" />
				</div>
			</div>
		</form>
	);
}
